import { motion } from 'framer-motion'

const RoastPhoneMockup = () => {
  return (
    <div className="relative">
      <div className="w-80 h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl border-2 border-gray-700">
        <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden border border-gray-800">
          <motion.div 
            className="p-5"
            animate={{ y: [0, -300, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
          >
            {/* Status Bar */}
            <div className="flex justify-between items-center mb-6 text-sm font-semibold text-white">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
              </div>
            </div>

            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="flex items-center justify-between mb-6"
            >
              <div>
                <div className="text-xl font-bold text-white">AI Assistant</div>
                <div className="text-xs text-orange-400 font-medium">🔥 Roast Mode</div>
              </div>
              <motion.div
                animate={{ rotate: [0, 360] }}
                transition={{ delay: 1, duration: 1, repeat: Infinity, repeatDelay: 5 }}
                className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-400 to-yellow-500 flex items-center justify-center text-xl"
              >
                🔥
              </motion.div>
            </motion.div>

            {/* Chat Messages */}
            <div className="space-y-4 mb-6">
              {/* User Message */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="flex justify-end"
              >
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-white">Why did I spend so much this month?</p>
                </div>
              </motion.div>

              {/* AI Roast Response */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.8, duration: 0.5 }}
                className="flex justify-start"
              >
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] border border-orange-200">
                  <p className="text-sm text-gray-800 font-medium">
                    Oh, where do I even start? 🙄 You ordered takeout <span className="font-bold text-orange-600">12 times</span> this month. That's almost every other day! Ever heard of a kitchen?
                  </p>
                </div>
              </motion.div>

              {/* Typing Indicator */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: [0, 1, 1, 0] }}
                transition={{ delay: 1.5, duration: 2, repeat: Infinity, repeatDelay: 4 }}
                className="flex justify-start"
              >
                <div className="bg-white/10 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex gap-1">
                    <motion.div
                      className="w-2 h-2 bg-orange-400 rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
                    />
                    <motion.div
                      className="w-2 h-2 bg-orange-400 rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
                    />
                    <motion.div
                      className="w-2 h-2 bg-orange-400 rounded-full"
                      animate={{ scale: [1, 1.3, 1] }}
                      transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
                    />
                  </div>
                </div>
              </motion.div>

              {/* Second AI Message */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 2.2, duration: 0.5 }}
                className="flex justify-start"
              >
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] border border-orange-200">
                  <p className="text-sm text-gray-800 font-medium">
                    Plus, you've got <span className="font-bold text-orange-600">3 streaming subscriptions</span> you haven't used in months. Netflix, Disney+, AND Amazon Prime? Pick one! 🎬
                  </p>
                </div>
              </motion.div>

              {/* User Reaction */}
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 2.8, duration: 0.5 }}
                className="flex justify-end"
              >
                <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl rounded-tr-sm px-4 py-3 max-w-[80%]">
                  <p className="text-sm text-white">Okay, point taken... 😅</p>
                </div>
              </motion.div>

              {/* AI Encouraging Message */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 3.2, duration: 0.5 }}
                className="flex justify-start"
              >
                <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-2xl rounded-tl-sm px-4 py-3 max-w-[85%] border border-orange-200">
                  <p className="text-sm text-gray-800 font-medium">
                    But real talk? You can save <span className="font-bold text-green-600">€180/month</span> if you fix these habits. I believe in you! 💪
                  </p>
                </div>
              </motion.div>
            </div>

            {/* Personality Selector */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.8, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-xs font-bold text-gray-300 mb-3">AI Personality</div>
              <div className="grid grid-cols-3 gap-2">
                {/* Professional */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-3 text-center cursor-pointer"
                >
                  <div className="text-2xl mb-1">💼</div>
                  <div className="text-xs font-semibold text-gray-300">Professional</div>
                </motion.div>

                {/* Friendly */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/5 border border-white/10 rounded-xl p-3 text-center cursor-pointer"
                >
                  <div className="text-2xl mb-1">😊</div>
                  <div className="text-xs font-semibold text-gray-300">Friendly</div>
                </motion.div>

                {/* Roast - Selected */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-gradient-to-r from-orange-500 to-yellow-500 rounded-xl p-3 text-center cursor-pointer"
                  animate={{ 
                    boxShadow: [
                      '0 0 0px rgba(249, 115, 22, 0)',
                      '0 0 20px rgba(249, 115, 22, 0.4)',
                      '0 0 0px rgba(249, 115, 22, 0)'
                    ]
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <div className="text-2xl mb-1">🔥</div>
                  <div className="text-xs font-bold text-white">Roast</div>
                </motion.div>
              </div>
            </motion.div>

            {/* Quick Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 4.2, duration: 0.5 }}
              className="bg-gradient-to-r from-orange-500/10 to-yellow-500/10 border border-orange-500/20 rounded-2xl p-4"
            >
              <div className="text-xs font-bold text-orange-400 mb-3">💡 Today's Roast Insights</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-2xl font-bold text-white">12x</div>
                  <div className="text-xs text-gray-400">Takeout Orders</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-white">€180</div>
                  <div className="text-xs text-gray-400">Potential Savings</div>
                </div>
              </div>
            </motion.div>

            {/* Extra spacing for scroll animation */}
            <div className="h-32"></div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default RoastPhoneMockup
